// from https://developers.klaviyo.com/en/docs/track_klaviyo_form_activity_using_javascript

import {readCachedLocalStorage, STORAGE_CACHE_KEYS, writeCachedLocalStorage} from "../Session/cachedLocalStorage";
import {getTaggedLogger} from "../Session/taggedLogging";
import {getDispatch} from "../Utility/ReduxHelper";
import {setEmailUser, writeUserToLocalStorage} from "../ReduxStore/userSlice";
import {setPopupClosed, setPopupOpen} from "../lib/popupStatus";

const STORAGE_KEY = STORAGE_CACHE_KEYS.KLAVIYO_FORMS; // klaviyoFormHistory

const logger = getTaggedLogger('klaviyo');

/**
 * Called once by KlaviyoInformant at application startup, this will register an event
 * handler.
 *
 * @param callback
 */
export function trapKlaviyoFormEvents({callback} = {}) {

  window.addEventListener("klaviyoForms", function (e) {
    logger.log(`Klaviyo Form Event: ${JSON.stringify(e, null, 2)}`, e);

    // Automatically call our standard handler, which will update the
    handleKlaviyoFormEvent(e);

    // ...and an optional callback.
    if (callback) {
      callback(e);
    }
  });
}

export function readKlaviyoFormHistory() {
  return readCachedLocalStorage(STORAGE_KEY, {defaultValue: {}});
}

export function writeKlaviyoFormHistory(formStatus) {
  writeCachedLocalStorage(STORAGE_KEY, formStatus);

  logger.info("updated klaviyo status: ", formStatus);
  return formStatus;
}

export function handleKlaviyoFormEvent(event) {
  event ||= {};

  // add to our Klaviyo history in localStorage
  let entry = updateKlaviyoFormHistoryFromEvent(event);

  // update cached user in local storage for future identify() calls
  updateCachedUserFromEvent(event);

  // and update popupsOpen = { klaviyo-ID => true } in Redux
  updatePopupStatusFromEvent(event);

  return entry;
}

function updateCachedUserFromEvent(event) {
  if (!event) return null;

  const {detail = {}} = event;

  const {metaData = {}} = detail;
  const {$email: email, $phone_number: phone} = (metaData || {});

  // got an email address? Save to localStorage right away,
  // so future klaviyo identify() calls will get it
  if (!email) return null;

  saveLocalUserFromKlaviyo({
    email, phone, source: 'klaviyo'
  });
}

function updatePopupStatusFromEvent(event) {
  if (!event) return null;

  const {detail = {}} = event;

  const {formId, type: eventType} = detail;

  if (!formId) {
    logger.error("no form ID in klaviyo event: ", event);
    return;
  }

  const popupName = `klaviyo-${formId}`;
  if (eventType === 'close') {
    setPopupClosed(popupName);
  } else {
    setPopupOpen(popupName);
  }

}

function updateKlaviyoFormHistoryFromEvent(event) {
  if (!event) return null;

  let now = new Date();

  const {detail = {}} = event;

  const {formId, type: eventType, metaData = {}} = detail;
  const {$email, $phone_number} = (metaData || {});

  if (!formId) {
    logger.error("no form ID in klaviyo event: ", event);
    return;
  }

  let formStatus = readKlaviyoFormHistory();

  let entry = (formStatus[formId] ||= {formId, openCount: 0, touchedAt: now.getTime()});

  // maintain a list of events that have happened
  entry.eventList ||= [];
  entry.eventList.push(eventType);

  if ($email) {
    entry.email = $email;
  }

  if ($phone_number) {
    entry.phoneNumber = $phone_number;
  }

  switch (eventType) {
    case "open":
      entry.status = 'open';
      entry.openCount = 1 + (+entry.openCount);
      entry.openedAt = now.getTime();
      //entry.eventList = [ 'open' ];  // and reset eventList
      break;
    case "submit":  // typically followed soon by 'close'
      entry.status = 'submitted';
      entry.submitted = true;
      break;
    case "close":
      entry.status = 'closed';
      entry.closedAt = now.getTime();
      break;
    default:
      logger.info(`ignoring klaviyo form event ${eventType} on form ${formId}`)
  }

  writeKlaviyoFormHistory(formStatus);
  return entry;
}

function saveLocalUserFromKlaviyo(user = {}) {
  const {email} = user;
  if (!email) {
    logger.warn("saveLocalUser: email is blank")
  }

  try {
    writeUserToLocalStorage(user);

    let dispatch = getDispatch();
    dispatch(setEmailUser(user))
  } catch (err) {
    logger.error("saveLocalUserFromKlaviyo error: " + err)
  }
}

/*
 // klaviyo.openForm('SAraHJ')

 grab event.detail from each:
 good events:
 stepSubmit - multiple times per form
 submit - once, has main payload

 detail.metaData.{email, phone_number, stepName, ...additional fields}
 */
